import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';
import {ColumnInstance, Row, TableInstance} from 'react-table';

import List, {ListItem} from 'app/components/List';
import {AlertPolicy, ApiFeature} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganizationUser, TagSetting} from 'app/modules/Remote/Organization/types';
import colors from 'app/styles/colors.json';
import * as tagUtils from 'app/utils/tagUtils';

import type {ImageryFilterValue, NoteCountFilterValue, TagFilterValue} from './filterUtils';
import {
  TagFilterContent,
  assigneeFilterBaseListItems,
  dateFilterListItems,
  decodeTagFilterValue,
  encodeTagFilterValue,
  imageryFilterListItems,
  mapAllAnyNoneMenuIdToFilterValue,
  mapNoteCountMenuIdToFilterValue,
  noteCountFilterListItems,
  shouldResetTagFilter,
} from './filterUtils';
import cs from './PropertyOverview.module.scss';
import {AdditionalFeaturePropertyFilterMenuBody} from './PropertyOverviewFiltersMenu';
import type {PropertyTableRow} from './PropertyTableTypes';
import {getAlertListItems, makeUserListItems} from './utils';

export type {ImageryFilterValue, NoteCountFilterValue, TagFilterValue} from './filterUtils';
export {decodeTagFilterValue, encodeTagFilterValue};

export type FilterProps<D extends object = PropertyTableRow> = Pick<
  TableInstance<D>,
  'setFilter'
> & {
  column: Pick<ColumnInstance<D>, 'id' | 'filterValue'>;
};

/**
 * Filter component for a react-table Column.  Used for the "Name" search box.
 *
 * "Pick" in Props to make it more obvious what parts of the react-table objects
 * need to be provided in stories.
 */
export function SearchBoxFilter<D extends object = PropertyTableRow>({
  column,
  setFilter,
  placeholder = 'Search properties…',
}: React.PropsWithChildren<FilterProps<D> & {placeholder?: string}>) {
  const queryFieldRef = React.useRef<HTMLInputElement>(null);
  const query = column.filterValue || '';

  const hotkeys = React.useMemo(
    () => [
      {
        global: true,
        combo: '/',
        label: 'Search',
        onKeyDown: (ev) => {
          ev.preventDefault();
          queryFieldRef?.current?.focus();
        },
      },
    ],
    []
  );

  const onChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(column.id, ev.currentTarget.value);
    },
    [setFilter, column.id]
  );

  const onKeyDown = React.useCallback((ev: React.KeyboardEvent) => {
    if (queryFieldRef.current && ev.key === 'Escape') {
      queryFieldRef.current.blur();
    } else if (ev.key === 'ArrowDown') {
      // A bit of a hack that we go up to the DOM for this, but it’s somewhat
      // better than the effort of trying to push a function through to here
      // that would basically just do the same thing.
      const firstRow = document.getElementsByClassName(cs.row)[0] as HTMLElement | undefined;

      if (firstRow) {
        firstRow.focus();
      }

      ev.preventDefault();
    }
  }, []);

  const {handleKeyDown, handleKeyUp} = B.useHotkeys(hotkeys);

  const rightElement = query.length ? (
    <B.Button
      minimal={true}
      icon="delete"
      onClick={() => {
        setFilter(column.id, '');

        // We need to re-focus to keep keyboard events going
        // through the input group.
        if (queryFieldRef.current) {
          queryFieldRef.current.focus();
        }
      }}
    />
  ) : undefined;

  return (
    <div onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
      <B.InputGroup
        className={cs.propertySearchInput}
        inputRef={queryFieldRef as React.RefObject<HTMLInputElement>}
        leftIcon={<B.Icon icon="search" color={colors.darkestGray} />}
        placeholder={placeholder}
        dir="auto"
        value={query}
        onChange={onChange}
        onKeyDown={onKeyDown}
        rightElement={rightElement}
      />
    </div>
  );
}

/**
 * "make" function so we can parameterize it over whether or not we’re treating
 * hidden imagery as available.
 */
export const makeTagsFilter =
  (tagSettings: I.OrderedMap<string, I.ImmutableOf<TagSetting>>) =>
  (rows: Row<PropertyTableRow>[], _, filterValue = ''): Row<PropertyTableRow>[] => {
    const value = decodeTagFilterValue(filterValue, tagSettings);
    return rows.filter((r) => featureTagFilter(r.original.features, value));
  };

export function featureTagFilter(
  features: I.ImmutableOf<ApiFeature[]>,
  value: TagFilterValue
): boolean {
  if (value.tagIds.isEmpty()) {
    return true;
  }

  // A list where each element is a set of tag IDs set on that feature.
  // Multi-location property rows will have multiple elements in this list.
  const tagIdsPerFeature = features.map((feature) => tagUtils.getFeatureTagIds(feature!));

  if (value.op === 'any') {
    return tagIdsPerFeature.some((tagIds) => !tagIds!.intersect(value.tagIds).isEmpty());
  } else if (value.op === 'all') {
    return tagIdsPerFeature.some((tagIds) => tagIds!.isSuperset(value.tagIds));
  } else if (value.op === 'none') {
    return tagIdsPerFeature.some((tagIds) => tagIds!.intersect(value.tagIds).isEmpty());
  } else {
    return true;
  }
}

export const TableTagFilter: React.FunctionComponent<
  React.PropsWithChildren<
    FilterProps & {
      tagSettings: I.OrderedMap<string, I.ImmutableOf<TagSetting>>;
    }
  >
> = ({column, setFilter, tagSettings}) => {
  const value = decodeTagFilterValue(column.filterValue, tagSettings);
  const setValue = (v: TagFilterValue | undefined) => {
    if (shouldResetTagFilter(v)) {
      return setFilter('tags', undefined);
    }
    return setFilter('tags', encodeTagFilterValue(v!));
  };

  return (
    <TagFilter
      tagSettings={tagSettings}
      value={value}
      setValue={setValue}
      showTooltip={false}
      popoverProps={{usePortal: false}}
    />
  );
};

export const TagFilter: React.FunctionComponent<
  React.PropsWithChildren<{
    tagSettings: I.OrderedMap<string, I.ImmutableOf<TagSetting>>;
    value: TagFilterValue;
    setValue: (v: TagFilterValue | undefined) => void;
    openPopoverForTest?: boolean;
    minimal?: boolean;
    showTooltip?: boolean;
    popoverProps?: B.PopoverProps;
  }>
> = ({
  tagSettings,
  value,
  setValue,
  openPopoverForTest,
  minimal = true,
  showTooltip = true,
  popoverProps,
}) => {
  return (
    <B.Popover
      position="bottom"
      isOpen={openPopoverForTest}
      {...popoverProps}
      content={<TagFilterContent tagSettings={tagSettings} value={value} setValue={setValue} />}
    >
      <B.Tooltip content="Filter by tags" disabled={!showTooltip}>
        <B.AnchorButton
          minimal={minimal}
          active={!!value.tagIds.size}
          icon={<B.Icon icon="filter" color={colors.darkestGray} />}
          small
        />
      </B.Tooltip>
    </B.Popover>
  );
};

export const ImageryStatusFilter: React.FunctionComponent<
  React.PropsWithChildren<
    FilterProps & {
      isHiddenImageryShownAsAvailable: boolean;
      setIsHiddenImageryShownAsAvailable: (show: boolean) => void;
      openPopoverForTest?: boolean;
    }
  >
> = ({
  column,
  setFilter,
  isHiddenImageryShownAsAvailable,
  setIsHiddenImageryShownAsAvailable,
  openPopoverForTest,
}) => {
  const value: ImageryFilterValue = column.filterValue || 'all';

  return (
    <B.Popover
      position={B.Position.BOTTOM}
      usePortal={false}
      isOpen={openPopoverForTest}
      content={
        <List
          items={imageryFilterListItems(value, isHiddenImageryShownAsAvailable)}
          onClickItem={({id}) => {
            if (id === 'hidden') {
              setIsHiddenImageryShownAsAvailable(!isHiddenImageryShownAsAvailable);
            } else {
              // For "all" we clear, rather than having "#imagery=all" in the
              // location field
              setFilter(column.id, id === 'all' ? undefined : id);
            }
          }}
        />
      }
    >
      <B.Button
        minimal
        active={value !== 'all'}
        icon={<B.Icon icon="filter" color={colors.darkestGray} />}
        small
      />
    </B.Popover>
  );
};

// TODO(fiona): This should also show email addresses that have been assigned to
// but don’t have an org user.
export const AssigneeFilter: React.FunctionComponent<
  React.PropsWithChildren<
    FilterProps & {
      organizationUsers: ApiOrganizationUser[];
      openPopoverForTest?: boolean;
    }
  >
> = ({column, setFilter, organizationUsers, openPopoverForTest}) => {
  const value: string | null = column.filterValue === undefined ? null : column.filterValue;

  return (
    <B.Popover
      position={B.Position.BOTTOM}
      usePortal={false}
      isOpen={openPopoverForTest}
      content={
        <List
          className={cs.actionMenu}
          items={[
            ...assigneeFilterBaseListItems(value),
            ...makeUserListItems(organizationUsers, (u) => value === u.email),
          ]}
          onClickItem={({id}) => {
            setFilter(column.id, mapAllAnyNoneMenuIdToFilterValue(id));
          }}
        />
      }
    >
      <B.Button
        minimal
        active={value !== null}
        icon={<B.Icon icon="filter" color={colors.darkestGray} />}
        small
      />
    </B.Popover>
  );
};

export const DateFilter: React.FunctionComponent<
  React.PropsWithChildren<
    FilterProps & {
      openPopoverForTest?: boolean;
    }
  >
> = ({column, setFilter, openPopoverForTest}) => {
  const value: string | null = column.filterValue === undefined ? null : column.filterValue;

  return (
    <B.Popover
      position={B.Position.BOTTOM}
      usePortal={false}
      isOpen={openPopoverForTest}
      content={
        <List
          className={cs.actionMenu}
          items={dateFilterListItems(value)}
          onClickItem={({id}) => {
            setFilter(column.id, mapAllAnyNoneMenuIdToFilterValue(id));
          }}
        />
      }
    >
      <B.Button
        minimal
        active={value !== null}
        icon={<B.Icon icon="filter" color={colors.darkestGray} />}
        small
      />
    </B.Popover>
  );
};

export const NoteCountFilter: React.FunctionComponent<
  React.PropsWithChildren<
    FilterProps & {
      openPopoverForTest?: boolean;
    }
  >
> = ({column, setFilter, openPopoverForTest}) => {
  const value: NoteCountFilterValue = column.filterValue === undefined ? null : column.filterValue;

  return (
    <B.Popover
      position={B.Position.BOTTOM}
      isOpen={openPopoverForTest}
      usePortal={false}
      content={
        <List
          className={cs.actionMenu}
          items={noteCountFilterListItems(value)}
          onClickItem={({id}) => {
            setFilter(column.id, mapNoteCountMenuIdToFilterValue(id));
          }}
        />
      }
    >
      <B.Button
        minimal
        active={value !== null}
        icon={<B.Icon icon="filter" color={colors.darkestGray} />}
        small
      />
    </B.Popover>
  );
};

export const AdditionalFeaturePropertyFilter: React.FunctionComponent<
  React.PropsWithChildren<
    FilterProps & {
      table: TableInstance<PropertyTableRow>;
      openPopoverForTest?: boolean;
    }
  >
> = ({column, table, openPopoverForTest}) => {
  const value = column.filterValue === undefined ? null : column.filterValue;
  return (
    <B.Popover
      position={B.Position.BOTTOM}
      usePortal={false}
      isOpen={openPopoverForTest}
      content={<AdditionalFeaturePropertyFilterMenuBody column={column} table={table} />}
    >
      <B.Button
        minimal
        active={value !== null}
        icon={<B.Icon icon="filter" color={colors.darkestGray} />}
        small
      />
    </B.Popover>
  );
};

export type AlertCountFilterValue = I.Set<string>;

export function encodeAlertCountFilterValue(filterValue: AlertCountFilterValue) {
  return `${filterValue.toArray().join(',')}`;
}

export function decodeAlertCountFilterValue(filterString: string | undefined) {
  return filterString ? I.Set(filterString.split(',')) : I.Set([]);
}

export const AlertCountFilter: React.FunctionComponent<
  React.PropsWithChildren<
    FilterProps & {
      openPopoverForTest?: boolean;
      selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
      alertPolicies: AlertPolicy[];
      parcelAlertsInPlan: boolean;
    }
  >
> = ({
  column,
  setFilter,
  openPopoverForTest,
  alertPolicies,
  selectedFeatureCollection,
  parcelAlertsInPlan,
}) => {
  const value = decodeAlertCountFilterValue(column.filterValue);

  return (
    <B.Popover
      position={B.Position.BOTTOM}
      isOpen={openPopoverForTest}
      usePortal={false}
      content={
        <List
          className={cs.actionMenu}
          items={
            [
              ...getAlertListItems(
                'none', // First two props aren't relevant for us because we aren't using icon info
                'none',
                'none',
                selectedFeatureCollection,
                alertPolicies,
                I.List([]),
                parcelAlertsInPlan
              ).map((item) => ({
                id: item.id,
                text: item.text,
                icon: value.has(item.id) ? 'tick' : 'blank',
              })),
              {id: 'sep1', text: '', isSeparator: true, icon: 'blank'},
              {id: 'reset', text: 'Reset filter', icon: 'blank'},
            ] as ListItem<{id: string}>[]
          }
          onClickItem={({id}, ev) => {
            ev.preventDefault();

            if (id === 'reset') {
              setFilter(column.id, undefined);
            } else {
              const isSelected = value.has(id);

              if (isSelected) {
                setFilter(column.id, encodeAlertCountFilterValue(value.remove(id)));
              } else {
                setFilter(column.id, encodeAlertCountFilterValue(value.add(id)));
              }
            }
          }}
        />
      }
    >
      <B.Button
        minimal
        active={!!value?.size}
        icon={<B.Icon icon="filter" color={colors.darkestGray} />}
        small
      />
    </B.Popover>
  );
};
